// CreateEditProductDialog.tsx
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
} from "@mui/material";
import { useEffect } from "react";
import FormTextField from "../../../components/elements/FormTextField";
import { UNITS } from "../../../config/config";
import {
  Category,
  Product,
  Station,
} from "../../../data/models/product/ProductModel";
import { currencyTransform, formatCurrency } from "../../../utils/StringTool";
import { useFormWithNavigation } from "../../hooks/useFormWithNavigation";

interface Props {
  open: boolean;
  onClose: () => void;
  categories: Category[];
  stations: Station[];
  initialValue?: Product;
  onSave: (
    data: Omit<Product, "id"> | Partial<Omit<Product, "id">>
  ) => Promise<void> | void;
  loading: boolean;
}

export default function CreateEditProductDialog({
  open,
  onClose,
  categories,
  stations,
  initialValue,
  onSave,
  loading,
}: Props) {
  const initial: Partial<Product> = {
    name: "",
    categoryId: categories[0]?.id ?? "",
    stations: [],
    unit: "UNIT",
    minQuantity: 1,
    cost: 0,
    salePrice: 0,
  };

  const validate = (
    field: keyof typeof initial,
    value: any,
    form: typeof initial
  ) => {
    if (field === "name" && !String(value).trim())
      return "El nombre es obligatorio";

    if (field === "categoryId" && !value) return "Seleccione una categoría";

    if (field === "unit" && !value) return "Seleccione una medida";

    if (field === "minQuantity" && (!value || value < 1))
      return "Cantidad mínima inválida";

    if (field === "cost" && (!value || value <= 0)) return "Costo inválido";

    if (field === "salePrice" && (!value || value <= 0))
      return "Precio inválido";

    if (field === "salePrice" && value && form.cost && value <= form.cost)
      return "El precio debe ser mayor al costo";

    return "";
  };

  const {
    form,
    setForm,
    errors,
    refs,
    handleChange,
    handleEnter,
    validateAll,
  } = useFormWithNavigation(initial, validate, { autofocus: true });

  useEffect(() => {
    if (open && !initialValue) {
      setForm(initial);
      setTimeout(() => {
        const firstKey = Object.keys(refs)[0] as keyof typeof initial;
        refs[firstKey]?.current?.focus();
      }, 50);
    }
  }, [open, initialValue]);

  useEffect(() => {
    if (open && initialValue) {
      const { id, ...rest } = initialValue;
      setForm(rest);
    }
  }, [open, initialValue]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!validateAll()) return;
    await onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {initialValue ? "Editar producto" : "Nuevo producto"}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} direction="column">
            <Grid item xs={12}>
              <FormTextField
                label="Nombre"
                field="name"
                form={{ ...form, name: form.name }}
                errors={errors}
                refs={refs}
                onChange={handleChange("name", (v) => v.toUpperCase())}
                onKeyDown={handleEnter("name")}
              />
            </Grid>

            <Grid item xs={12}>
              <FormTextField
                label="Categoría"
                field="categoryId"
                select
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("categoryId")}
                onKeyDown={handleEnter("categoryId")}
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>

            <Grid item xs={12}>
              <FormTextField
                label="Pantallas"
                field="stations"
                select
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("stations", (st) =>
                  Array.isArray(st) ? st : [st]
                )}
                onKeyDown={handleEnter("stations")}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected: string[]) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((id) => {
                        const s = stations.find((st) => st.id === id);
                        return (
                          <Chip
                            key={id}
                            label={s?.name}
                            size="small"
                            sx={{ bgcolor: "primary.main", color: "white" }}
                          />
                        );
                      })}
                    </Box>
                  ),
                }}
              >
                {stations.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>

            <Grid item xs={12}>
              <FormTextField
                label="Medida"
                field="unit"
                select
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("unit")}
                onKeyDown={handleEnter("unit")}
              >
                {UNITS.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>

            <Grid item xs={12}>
              <FormTextField
                label="Cantidad mínima"
                field="minQuantity"
                type="number"
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("minQuantity", (v) => Number(v || 0))}
                onKeyDown={handleEnter("minQuantity")}
              />
            </Grid>

            <Grid item xs={12}>
              <FormTextField
                label="Costo"
                field="cost"
                form={{ ...form, cost: formatCurrency(form.cost!) }}
                errors={errors}
                refs={refs}
                onChange={handleChange("cost", currencyTransform)}
                onKeyDown={handleEnter("cost")}
              />
            </Grid>

            <Grid item xs={12}>
              <FormTextField
                label="Precio de venta"
                field="salePrice"
                form={{
                  ...form,
                  salePrice: formatCurrency(form.salePrice!),
                }}
                errors={errors}
                refs={refs}
                onChange={handleChange("salePrice", currencyTransform)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" type="submit" loading={loading}>
            {initialValue ? "Guardar cambios" : "Crear producto"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
